import { useEffect, useState } from 'react';

import { CheckmarkCircle04Icon } from '@hugeicons-pro/core-stroke-rounded';
import { AddCircleIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { useLocale } from 'hooks/locale';
import { useToast } from 'hooks/toast';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { NewEmployeeModal } from 'views/Renewals/components/NewEmployeeModal/NewEmployeeModal';
import { RenewalBulkConfigurationModal } from 'views/Renewals/components/RenewalBulkConfigurationModal/RenewalBulkConfigurationModal';

import { Button } from 'components/FormComponents';
import { Select } from 'components/FormComponents';
import { Table } from 'components/Table/Table';
import { VerificationBadge } from 'components/VerificationBadge/VerificationBadge';

import { getCustomFields, selectEmployeeCustomFields } from 'api/candidate-custom-fields-slice';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectCompany } from 'api/company-slice';
import {
  useBulkAddCheckRenewalsToEmployeesMutation,
  useCreateEmployeeMutation,
  useLazyGetEmployeesQuery,
  useUpdateEmployeeMutation,
} from 'api/employees-api';
import { useGetRenewalConfigsQuery } from 'api/renewals-config-api';
import { getTags, selectTags } from 'api/tags-slice';
import { useGetCompanyUsersQuery, useGetUserTeamsQuery } from 'api/user-api';
import { selectUser } from 'api/user-slice';

import styles from './RenewalsEmployeesTab.module.scss';

export const RenewalsEmployeesTab = ({ setLoading = () => {} }) => {
  const dispatch = useDispatch();
  const showToast = useToast();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const company = useSelector(selectCompany);
  const companyChecks = useSelector(selectCompanyChecks);
  const customFields = useSelector(selectEmployeeCustomFields);

  const user = useSelector(selectUser);
  const tags = useSelector(selectTags);
  const { data: renewalConfigs } = useGetRenewalConfigsQuery();
  const { data: teams } = useGetUserTeamsQuery(user.id);
  const { data: companyUsers } = useGetCompanyUsersQuery(user?.id, {
    refetchOnMountOrArgChange: true,
  });

  const [updateEmployee] = useUpdateEmployeeMutation();
  const [getEmployees] = useLazyGetEmployeesQuery();
  const [bulkAddCheckRenewalsToEmployees] = useBulkAddCheckRenewalsToEmployeesMutation();
  const [createEmployee] = useCreateEmployeeMutation();

  const [pagination, setPagination] = useState({ page: 1, total_pages: 1 });
  const [employees, setEmployees] = useState([]);
  const { dateSlashFormat } = useLocale(company?.locale);

  const [showNewEmployeeModal, setShowNewEmployeeModal] = useState(false);

  const [showBulkConfigurationModal, setShowBulkConfigurationModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkUseAll, setBulkUseAll] = useState(false);

  useEffect(() => {
    dispatch(getTags());
    dispatch(getCustomFields());
  }, []);

  const onQueryUpdate = async params => {
    setLoading(true);
    const { data: result } = await getEmployees({ params });
    setEmployees(result.employees);
    setPagination(result.pagination);
    setLoading(false);
  };

  const updateEmployeeHired = async (id, active) => {
    setLoading(true);
    await updateEmployee({ id, data: { active } });
    setEmployees(
      employees.map(employee => (employee.id === id ? { ...employee, active } : employee)),
    );
    setLoading(false);
  };

  const handleBulkAddCheckRenewalsToEmployees = async renewalChecks => {
    setLoading(true);
    await bulkAddCheckRenewalsToEmployees({
      employee_ids: selectedIds,
      use_all: bulkUseAll,
      renewal_checks: renewalChecks,
    });
    showToast(
      `${selectedIds.length} employees configured with new renewals.`,
      `Renewals will be added to the employees soon!`,
    );
    setShowBulkConfigurationModal(false);
    setLoading(false);
  };

  const handleEmployeeCreation = async employeeDetails => {
    setLoading(true);
    const { data: employee, error } = await createEmployee(employeeDetails);
    if (error) {
      showToast('Error', error.data.error.log, 'error');
      setLoading(false);
      return;
    }
    setShowNewEmployeeModal(false);
    navigate(`/employee/${employee.id}/renewals/configure`);
    setLoading(false);
  };

  return (
    <>
      <RenewalBulkConfigurationModal
        visible={showBulkConfigurationModal}
        onClose={() => setShowBulkConfigurationModal(false)}
        renewalConfigs={renewalConfigs}
        companyChecks={companyChecks}
        onSubmit={handleBulkAddCheckRenewalsToEmployees}
      />
      <NewEmployeeModal
        visible={showNewEmployeeModal}
        company={company}
        tags={tags}
        teams={teams}
        customFields={customFields}
        users={companyUsers}
        createEmployee={handleEmployeeCreation}
        onClose={() => setShowNewEmployeeModal(false)}
      />
      <div className="card card-with-border u-padding d-flex justify-content-end u-margin--large">
        <Button icon="user-plus" onClick={() => setShowNewEmployeeModal(true)}>
          Add New Employee
        </Button>
        <Button
          icon="file"
          type="secondary"
          onClick={() => navigate('/bulk_import/renewals')}
          className="u-margin-left--small">
          {t('renewals.tabs.configuration.import_employees')}
        </Button>
      </div>
      <Table.Context
        onQueryUpdate={onQueryUpdate}
        defaultQuery={{ page: 1, when: '30' }}
        onSelectedIdsChange={setSelectedIds}
        onBulkUseAllChange={setBulkUseAll}>
        <Table.TableContent className="u-margin--large">
          <Table.Filters>
            <Table.LeftFilters>
              <Table.Search placeholder="Search for an employee..." />
            </Table.LeftFilters>
            <Table.RightFilters>
              <Table.MultiSelectFilter
                title="Configured"
                filterName="configured"
                type="status"
                options={[
                  { name: 'Configured', value: 'true' },
                  { name: 'Not Configured', value: 'false' },
                ]}
              />
              <Table.MultiSelectFilter
                title="Active"
                filterName="active"
                type="status"
                options={[
                  { name: 'Active', value: 'true' },
                  { name: 'Inactive', value: 'false' },
                ]}
              />
            </Table.RightFilters>
          </Table.Filters>
          <Table className="u-margin--large">
            <Table.Head>
              <Table.Row>
                <Table.Header bulkSelector>Configured</Table.Header>
                <Table.Header>Employee</Table.Header>
                <Table.Header>Department</Table.Header>
                <Table.Header>Last Activity</Table.Header>
                <Table.Header>Active Renewals</Table.Header>
                <Table.Header>Overdue</Table.Header>
                <Table.Header>Upcoming</Table.Header>
                <Table.Header>Hired</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {employees.map(employee => (
                <Table.Row key={employee.candidate_id}>
                  <Table.Col
                    styles={{ 'vertical-align': 'center' }}
                    bulkSelector
                    selectorId={employee.id}>
                    <VerificationBadge
                      onClick={() =>
                        navigate(
                          `/employee/${employee.id}/renewals/${employee.configured ? '' : 'configure'}`,
                        )
                      }
                      type={employee.configured ? 'success' : 'warning'}
                      tip={employee.configured ? 'Configured' : 'Not Configured'}
                    />
                  </Table.Col>
                  <Table.Col>
                    <Link to={`/employee/${employee.id}`} className="u-link dark">
                      {employee.candidate_name}
                    </Link>
                  </Table.Col>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    {employee.department}
                  </Table.Col>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    {employee.last_activity ? (
                      <Moment format={dateSlashFormat}>{employee.last_activity}</Moment>
                    ) : (
                      '-'
                    )}
                  </Table.Col>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    {employee.renewals_count}
                  </Table.Col>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    {employee.overdue_count}
                  </Table.Col>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    {employee.upcoming_count}
                  </Table.Col>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    <Select
                      small
                      className={styles.hiringStatusSelect}
                      rounded
                      value={employee.active}
                      onChange={e => updateEmployeeHired(employee.id, e.target.value)}>
                      <Select.Item value={true}>
                        <div className="d-flex align-items-center">
                          <HugeiconsIcon
                            size="18"
                            icon={CheckmarkCircle04Icon}
                            strokeWidth="2"
                            className={cn('text-success', 'u-margin-right--small')}
                          />{' '}
                          Active
                        </div>
                      </Select.Item>
                      <Select.Item value={false}>
                        <div className="d-flex align-items-center">
                          <HugeiconsIcon
                            size="18"
                            icon={CheckmarkCircle04Icon}
                            strokeWidth="2"
                            className={cn('text-error', 'u-margin-right--small')}
                          />{' '}
                          Inactive
                        </div>
                      </Select.Item>
                    </Select>
                  </Table.Col>
                </Table.Row>
              ))}
              {employees.length < 1 && <Table.Placeholder>No employees found</Table.Placeholder>}
            </Table.Body>
            <Table.Pagination {...pagination} />
          </Table>
        </Table.TableContent>
        <Table.BulkActions>
          <Table.BulkAction
            icon={AddCircleIcon}
            tip="Add renewals"
            onClick={(ids, useAll) => setShowBulkConfigurationModal(true)}
          />
        </Table.BulkActions>
      </Table.Context>
    </>
  );
};
